import { useMemo, useRef, useState, type FormEvent } from 'react'
import { AlertCircle, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { PARTNER_CATEGORIES, type Customer, type CustomerPayload, type PartnerCategory } from '@/api/customersApi'
import { useCustomerMutations, useCustomersList } from '@/hooks/useCustomers'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import {
  categoryOf,
  customerDraft,
  customerPayload,
  filterCustomers,
  partnerAddressCell,
  validateCustomer,
  type CustomerDraft,
  type CustomerFormErrors,
} from '@/lib/customers'
import { PartnerAddressFields } from '@/components/admin/PartnerAddressFields'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// ─── Settings tab content ─────────────────────────────────────────────────────

// Per-user column layout. Code identifies the row and Actions holds the
// controls, so both are locked — see src/components/table/index.ts. The id
// predates the rename to Partners and stays, so saved layouts carry over.
const CUSTOMERS_TABLE_ID = 'customers'

// The spec's columns (z8nrz7earp), in its order, with its Country, State, City
// and Zip Code as one Address column (z8nrz7epeq): the address as it is written.
const CUSTOMERS_COLUMNS: TableColumnDef[] = [
  { id: 'code', header: 'Code', locked: true, defaultWidth: 110, sortable: true },
  { id: 'category', header: 'Category', defaultWidth: 130, sortable: true },
  { id: 'name', header: 'Name', defaultWidth: 200, sortable: true },
  { id: 'contactPerson', header: 'Contact Person', defaultWidth: 170, sortable: true },
  { id: 'address', header: 'Address', defaultWidth: 320, sortable: true },
  { id: 'phone', header: 'Phone', defaultWidth: 150, sortable: true },
  { id: 'email', header: 'Email', defaultWidth: 220, sortable: true },
  // 2 icon buttons at 40px + 4px gap + px-4 padding.
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 120 },
]

// Without customers.manage there is nothing to put in the Actions column.
const CUSTOMERS_COLUMNS_READ_ONLY = CUSTOMERS_COLUMNS.filter(c => c.id !== 'actions')

// The whole list is fetched at once, so header sorting is client-side.
const CUSTOMERS_SORT: SortAccessors<Customer> = {
  code: c => c.code,
  category: c => categoryOf(c),
  name: c => c.name,
  contactPerson: c => c.contactPerson,
  address: c => partnerAddressCell(c).text,
  phone: c => c.phone,
  email: c => c.email,
}

/** A detail cell: the value, or a dash. */
function Detail({ value, muted }: { value: string | null | undefined; muted?: boolean }) {
  return <TableCell className={muted ? 'text-muted-foreground' : undefined}>{value || '—'}</TableCell>
}

/**
 * Configure → Partners (z8nrz7earp; Customers in z8nrz7e6zn): who the tenant
 * works with, each as one or more of Customer, Vendor, Courier, Supplier and
 * Partner. The Customers among them are who Kit and MES runs are produced for.
 * Readable with customers.read; the create, edit and delete controls need
 * customers.manage.
 */
export function CustomersSettings() {
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.customersManage)
  const cols = useTableColumns(
    CUSTOMERS_TABLE_ID,
    canManage ? CUSTOMERS_COLUMNS : CUSTOMERS_COLUMNS_READ_ONLY,
  )

  const list = useCustomersList()
  const { create, update, remove } = useCustomerMutations()

  const [q, setQ] = useState('')
  const [category, setCategory] = useState<PartnerCategory | 'all'>('all')
  const all = list.data
  const filtered = useMemo(() => filterCustomers(all ?? [], q, category), [all, q, category])
  const rows = useSortedRows(filtered, cols.sort, CUSTOMERS_SORT)

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Customer | null>(null)
  const [deleting, setDeleting] = useState<Customer | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)

  const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

  function handleCreate(payload: CustomerPayload) {
    setServerError(null)
    create.mutate(payload, {
      onSuccess: () => setCreateOpen(false),
      onError: e => setServerError(errorText(e, 'Could not create partner.')),
    })
  }

  function handleUpdate(id: string, payload: CustomerPayload) {
    setServerError(null)
    update.mutate(
      { id, payload },
      {
        onSuccess: () => setEditing(null),
        onError: e => setServerError(errorText(e, 'Could not update partner.')),
      },
    )
  }

  function handleDelete(id: string) {
    setServerError(null)
    remove.mutate(id, {
      onSuccess: () => setDeleting(null),
      onError: e => setServerError(errorText(e, 'Could not delete partner.')),
    })
  }

  function closeDialogs() {
    setServerError(null)
    setCreateOpen(false)
    setEditing(null)
    setDeleting(null)
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center gap-3'>
        <div className='relative min-w-[240px] max-w-sm flex-1'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search by code, name or contact…'
            aria-label='Search partners'
            value={q}
            onChange={e => setQ(e.target.value)}
            className='pl-9'
          />
        </div>
        <Select value={category} onValueChange={v => setCategory(v as PartnerCategory | 'all')}>
          <SelectTrigger className='w-44 shrink-0' aria-label='Filter by category'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All categories</SelectItem>
            {PARTNER_CATEGORIES.map(c => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canManage && (
          <Button
            className='ml-auto'
            onClick={() => {
              setServerError(null)
              setCreateOpen(true)
            }}
          >
            <Plus className='mr-2 h-4 w-4' />
            Add Partner
          </Button>
        )}
      </div>

      <Card>
        <CardContent className='p-0'>
          {list.isLoading && (
            <div className='flex items-center justify-center py-20'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {list.isError && (
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              Could not load partners. {list.error.message}
            </div>
          )}

          {all && rows.length === 0 && (
            <div className='py-16 text-center text-sm text-muted-foreground'>
              {all.length > 0
                ? 'No partners match your search or category.'
                : canManage
                  ? 'No partners yet. Add one to get started.'
                  : 'No partners yet.'}
            </div>
          )}

          {all && rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  {cols.visible.map(({ def }) => (
                    <ResizableTableHead
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={def.id === 'actions' ? 'text-right' : undefined}
                      resizable={def.id !== 'actions'}
                    >
                      {def.header}
                    </ResizableTableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(c => {
                  const address = partnerAddressCell(c)
                  return (
                  <TableRow key={c.id}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        code: <TableCell className='font-medium'>{c.code}</TableCell>,
                        category: <TableCell>{categoryOf(c)}</TableCell>,
                        name: <TableCell>{c.name}</TableCell>,
                        contactPerson: <Detail value={c.contactPerson} muted />,
                        address: (
                          <TableCell>
                            {address.lines.length === 0 ? (
                              '—'
                            ) : (
                              <div className='flex items-start gap-2'>
                                {/* Two lines at most, each cut short rather than wrapped. */}
                                <div className='min-w-0 flex-1' title={address.lines.join('\n')}>
                                  {address.lines.map((line, i) => (
                                    <div key={i} className={i === 0 ? 'truncate' : 'truncate text-muted-foreground'}>
                                      {line}
                                    </div>
                                  ))}
                                </div>
                                {address.needsReview && (
                                  <span
                                    className='shrink-0 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700'
                                    title="This address does not fit its country's format. Edit the partner to fix it."
                                  >
                                    Needs review
                                  </span>
                                )}
                              </div>
                            )}
                          </TableCell>
                        ),
                        phone: <Detail value={c.phone} muted />,
                        email: <Detail value={c.email} muted />,
                        actions: (
                          <TableCell>
                            <div className='flex items-center justify-end gap-1'>
                              <Button
                                variant='ghost'
                                size='icon'
                                title='Edit partner'
                                onClick={() => {
                                  setServerError(null)
                                  setEditing(c)
                                }}
                              >
                                <Pencil className='h-4 w-4' />
                              </Button>
                              <Button
                                variant='ghost'
                                size='icon'
                                title='Delete partner'
                                onClick={() => {
                                  setServerError(null)
                                  setDeleting(c)
                                }}
                              >
                                <Trash2 className='h-4 w-4 text-destructive' />
                              </Button>
                            </div>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <CustomerFormDialog
          pending={create.isPending}
          serverError={serverError}
          onClose={closeDialogs}
          onSubmit={handleCreate}
        />
      )}

      {editing && (
        <CustomerFormDialog
          customer={editing}
          pending={update.isPending}
          serverError={serverError}
          onClose={closeDialogs}
          onSubmit={payload => handleUpdate(editing.id, payload)}
        />
      )}

      {deleting && (
        <Dialog open onOpenChange={o => !o && closeDialogs()}>
          <DialogContent className='sm:max-w-md'>
            <DialogHeader>
              <DialogTitle>Delete "{deleting.name}"?</DialogTitle>
              <DialogDescription>
                This removes partner {deleting.code} from this tenant. It cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {serverError && <p className='text-sm text-destructive'>{serverError}</p>}
            <DialogFooter>
              <Button type='button' variant='outline' onClick={closeDialogs} disabled={remove.isPending}>
                Cancel
              </Button>
              <Button
                type='button'
                variant='destructive'
                onClick={() => handleDelete(deleting.id)}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Delete partner'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

// ─── Create / Edit dialog ─────────────────────────────────────────────────────

type TextKey = 'code' | 'name' | 'contactPerson' | 'phone' | 'email'

/** The form's text fields, in rows: a pair shares a row, a single spans it. */
const FIELD_ROWS: { key: TextKey; label: string; required?: boolean; type?: string; autoComplete?: string }[][] = [
  [
    { key: 'code', label: 'Code', required: true },
    { key: 'name', label: 'Name', required: true },
  ],
  // Row 2 is Category (a dropdown) beside Contact person — see the form below.
  [{ key: 'contactPerson', label: 'Contact person' }],
  [
    { key: 'phone', label: 'Phone', type: 'tel', autoComplete: 'tel' },
    { key: 'email', label: 'Email', type: 'email', autoComplete: 'email' },
  ],
]

/**
 * The country a new partner's address starts in: the one used last in this
 * browser (z8nrz7epeq). A convenience only, so storage failures are ignored.
 */
const LAST_COUNTRY_KEY = 'cgt.partners.lastCountry'
function lastCountry(): string {
  try {
    return localStorage.getItem(LAST_COUNTRY_KEY) ?? ''
  } catch {
    return ''
  }
}
function rememberCountry(code: string) {
  try {
    if (code) localStorage.setItem(LAST_COUNTRY_KEY, code)
  } catch {
    // Private mode or blocked storage: the default simply is not remembered.
  }
}

function CustomerFormDialog({
  customer,
  pending,
  serverError,
  onClose,
  onSubmit,
}: {
  customer?: Customer
  pending: boolean
  serverError: string | null
  onClose: () => void
  onSubmit: (payload: CustomerPayload) => void
}) {
  const [draft, setDraft] = useState<CustomerDraft>(() => customerDraft(customer, lastCountry()))
  const [initialAddress] = useState(() => JSON.stringify(draft.address))
  // The address is checked and sent only when it was changed, or for a new
  // partner: an old address that needs review must not block other edits.
  const addressChanged = !customer || JSON.stringify(draft.address) !== initialAddress
  // Shown only after a submit attempt, then kept live as the user fixes them.
  const [touched, setTouched] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const allErrors = validateCustomer(draft, { checkAddress: addressChanged })
  const errors: CustomerFormErrors = touched ? allErrors : {}

  function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (Object.keys(allErrors).length > 0) {
      // Jump to the first problem, in the order the form shows them.
      requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
      return
    }
    if (addressChanged) rememberCountry(draft.address.countryCode)
    onSubmit(customerPayload(draft, { includeAddress: addressChanged }))
  }

  const field = (f: (typeof FIELD_ROWS)[number][number]) => {
    const id = `partner-${f.key}`
    const error = f.key === 'code' || f.key === 'name' ? errors[f.key] : undefined
    return (
      <div key={f.key} className='flex min-w-0 flex-1 flex-col gap-1.5'>
        <Label htmlFor={id}>
          {f.label}
          {f.required && <span className='text-destructive'> *</span>}
        </Label>
        <Input
          id={id}
          type={f.type ?? 'text'}
          value={draft[f.key]}
          autoFocus={f.key === 'code'}
          autoComplete={f.autoComplete ?? 'off'}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
        />
        {error && (
          <p id={`${id}-error`} className='text-xs text-destructive'>
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>{customer ? 'Edit partner' : 'New partner'}</DialogTitle>
          <DialogDescription>
            Code, name and a category are required. The code must be unique in this tenant.
          </DialogDescription>
        </DialogHeader>

        <form ref={formRef} onSubmit={submit} noValidate className='flex flex-col gap-4'>
          <div className='flex gap-3'>{FIELD_ROWS[0].map(field)}</div>

          <div className='flex gap-3'>
            <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
              <Label htmlFor='partner-category'>
                Category<span className='text-destructive'> *</span>
              </Label>
              <Select
                value={draft.category}
                onValueChange={v => setDraft(d => ({ ...d, category: v as PartnerCategory }))}
              >
                <SelectTrigger
                  id='partner-category'
                  aria-invalid={!!errors.category}
                  aria-describedby={errors.category ? 'partner-category-error' : undefined}
                >
                  <SelectValue placeholder='Select a category' />
                </SelectTrigger>
                <SelectContent>
                  {PARTNER_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && (
                <p id='partner-category-error' className='text-xs text-destructive'>
                  {errors.category}
                </p>
              )}
            </div>
            {field(FIELD_ROWS[1][0])}
          </div>

          {FIELD_ROWS.slice(2).map((row, i) => (
            <div key={i} className='flex gap-3'>
              {row.map(field)}
            </div>
          ))}

          <div className='flex flex-col gap-3 border-t pt-4'>
            <h3 className='text-sm font-semibold'>
              Address <span className='font-normal text-muted-foreground'>(optional)</span>
            </h3>
            <PartnerAddressFields
              value={draft.address}
              onChange={address => setDraft(d => ({ ...d, address }))}
              errors={allErrors.address ?? {}}
              showAllErrors={touched}
            />
          </div>

          {serverError && <p className='text-sm text-destructive'>{serverError}</p>}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending}>
              {pending ? 'Saving…' : customer ? 'Save changes' : 'Add partner'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
